import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';

import { AdminService } from '@generated/services/admin.service';
import { BenchmarkRunDetailDto } from '@generated/models/benchmark-run-detail-dto';
import { BenchmarkRunItemDetailDto } from '@generated/models/benchmark-run-item-detail-dto';
import { NotificationService } from '@core/services/notification.service';

/**
 * Правая drawer-панель с деталями одного BenchmarkRun'а.
 *
 * После SKN-290 update'а run стал GROUP: один run покрывает несколько
 * версий (BenchmarkRunItem). Соответственно detail = { run, items: [
 * { item: {...аггрегаты...}, results: [ per-iteration ] } ] }.
 *
 * Рендерим два уровня:
 *   1. Run header — общий статус, trigger, время, item-counters.
 *   2. Items list — для каждой версии свои аггрегаты (LLM: TTFT/TPS,
 *      STT: WER/CER/RTFx), раскрываются в per-iteration results (клик
 *      по chevron'у в правой части).
 */
@Component({
  selector: 'app-benchmark-run-details-drawer',
  standalone: true,
  imports: [
    DrawerModule,
    ButtonModule,
    TagModule,
    TableModule,
    ProgressSpinnerModule,
    TooltipModule,
  ],
  templateUrl: './benchmark-run-details-drawer.component.html',
  styleUrl: './benchmark-run-details-drawer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenchmarkRunDetailsDrawerComponent {
  private adminService = inject(AdminService);
  private notificationService = inject(NotificationService);

  readonly visible = input<boolean>(false);
  readonly runId = input<string | null>(null);
  readonly visibleChange = output<boolean>();

  readonly isLoading = signal<boolean>(false);
  readonly detail = signal<BenchmarkRunDetailDto | null>(null);
  /** Множество раскрытых item.id — юзер щёлкнул chevron, показываются
   *  per-iteration results. */
  readonly expandedItemIds = signal<Set<string>>(new Set());
  /** Множество раскрытых result.id — по умолчанию транскрипт (hypothesis
   *  для STT, completion для LLM) обрезается clamp'ом на 2 строки. Клик
   *  добавляет id сюда — показываем полный текст. */
  readonly expandedResultIds = signal<Set<string>>(new Set());

  readonly run = computed(() => this.detail()?.run ?? null);
  readonly items = computed<BenchmarkRunItemDetailDto[]>(() => this.detail()?.items ?? []);
  readonly modelTypeCode = computed(() => (this.run()?.model_type_code ?? '').toLowerCase());
  readonly isLlm = computed(() => this.modelTypeCode() === 'llm');
  readonly isStt = computed(() => this.modelTypeCode() === 'stt');

  constructor() {
    effect(() => {
      const id = this.runId();
      const isVisible = this.visible();
      if (!isVisible || !id) {
        this.detail.set(null);
        this.expandedItemIds.set(new Set());
        return;
      }
      this.loadRun(id);
    });
  }

  private loadRun(runId: string): void {
    this.isLoading.set(true);
    this.detail.set(null);
    this.expandedItemIds.set(new Set());
    this.adminService.apiAdminBenchmarkRunsRunIdGet({ runId }).subscribe({
      next: (data) => {
        this.detail.set(data);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load run detail:', error);
        this.notificationService.showError(
          $localize`:@@benchmarks.runDetails.loadFail:Не удалось загрузить прогон`
        );
        this.isLoading.set(false);
      }
    });
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  toggleItem(itemId: string | null | undefined): void {
    if (!itemId) return;
    const set = new Set(this.expandedItemIds());
    if (set.has(itemId)) set.delete(itemId); else set.add(itemId);
    this.expandedItemIds.set(set);
  }

  isItemExpanded(itemId: string | null | undefined): boolean {
    return !!itemId && this.expandedItemIds().has(itemId);
  }

  toggleResult(resultId: string | null | undefined): void {
    if (!resultId) return;
    const set = new Set(this.expandedResultIds());
    if (set.has(resultId)) set.delete(resultId); else set.add(resultId);
    this.expandedResultIds.set(set);
  }

  isResultExpanded(resultId: string | null | undefined): boolean {
    return !!resultId && this.expandedResultIds().has(resultId);
  }

  statusSeverity(s: string | null | undefined): 'info' | 'success' | 'warn' | 'danger' | 'secondary' {
    switch ((s || '').toLowerCase()) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'running': return 'warn';
      case 'initial': return 'info';
      case 'stopped': return 'secondary';
      default: return 'secondary';
    }
  }

  triggerLabel(t: string | null | undefined): string {
    switch ((t || '').toLowerCase()) {
      case 'manual': return $localize`:@@benchmarks.trigger.manual:Manual`;
      case 'scheduled': return $localize`:@@benchmarks.trigger.scheduled:Scheduled`;
      default: return t || '';
    }
  }

  caseLabel(code: string | null | undefined): string {
    switch ((code || '').toLowerCase()) {
      case 'quiet': return $localize`:@@benchmarks.stt.case.quiet:Тихо`;
      case 'roadside': return $localize`:@@benchmarks.stt.case.roadside:У дороги`;
      case 'car': return $localize`:@@benchmarks.stt.case.car:В машине`;
      case 'tv': return $localize`:@@benchmarks.stt.case.tv:У ТВ`;
      case 'speakerphone': return $localize`:@@benchmarks.stt.case.speaker:Громкая связь`;
      default: return code || '';
    }
  }

  formatMs(v: number | null | undefined): string { return v == null ? '—' : `${Math.round(v)} ms`; }
  formatTps(v: number | null | undefined): string { return v == null ? '—' : `${v.toFixed(1)} t/s`; }
  formatErrorRate(v: number | null | undefined): string {
    return v == null ? '—' : `${(v * 100).toFixed(1)}%`;
  }
  formatRtfx(v: number | null | undefined): string {
    return v == null ? '—' : `${v.toFixed(2)}×`;
  }
  formatDate(v: string | null | undefined): string {
    if (!v) return '';
    try { return new Date(v).toLocaleString(); } catch { return v; }
  }
}
